import { useEffect, useState } from 'react';
import { useDyteMeeting } from '@dytesdk/react-web-core';
import { Loader } from 'ui';
import { useCurrentTime } from 'src/hooks/utils/useCurrentTime';
import { AssessmentTokenDetails, MeetingStatus, UIMeeting, UserRole } from 'types';
import { ASSESS_PLATFORM_FRONTEND } from 'src/config/env.config';
import { useUserContext } from 'src/context/UserProvider';
import { useGetJwtTokenQuery } from 'src/hooks/user.hooks';
import { useGetMeetingScoreCard } from 'src/hooks/meeting.hooks';
import { useQueryClient } from 'react-query';
import ScorecardContainer from '../scorecard/ScorecardContainer';
import PostInterviewComponent from './PostInterviewComponent';

type Props = {
	meeting: UIMeeting;
	hasAssessmentEnded: boolean;
	ongoingAssessDetails?: AssessmentTokenDetails | null;
};

const QuestionsScreen = ({ meeting, hasAssessmentEnded, ongoingAssessDetails }: Props) => {
	const dyteMeeting = useDyteMeeting();
	const currentTime = useCurrentTime();
	const { user: session, isMentorLoggedIn } = useUserContext();
	const queryClient = useQueryClient();

	const [assessmentToken, setAssessmentToken] = useState<string>(ongoingAssessDetails?.token ?? '');

	const { data, isLoading, refetch } = useGetJwtTokenQuery(
		assessmentToken,
		data => {
			localStorage.removeItem('retryJWTSign');
		},
		() => {
			const retries = Number(localStorage.getItem('retryJWTSign'));
			if (retries < 3) {
				localStorage.setItem('retryJWTSign', String(retries + 1));
				refetch();
			}
		},
	);

	const { data: meetingScoreCardData, isLoading: scorecardLoading } = useGetMeetingScoreCard(meeting._id);

	useEffect(() => {
		// refetch();
		setAssessmentToken(ongoingAssessDetails?.token ?? '');
	}, [ongoingAssessDetails?.token]);

	useEffect(() => {
		queryClient.refetchQueries(['getMeetingScoreCard']);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasAssessmentEnded]);

	useEffect(() => {
		return () => {
			const meetingStartTime = new Date(meeting.startTime).getTime();
			const meetingDurationInMillis = meeting.duration * 60 * 1000;
			let endTime = meetingStartTime + meetingDurationInMillis;
			if (currentTime > endTime) dyteMeeting.meeting.leaveRoom();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (isLoading || (!hasAssessmentEnded && !ongoingAssessDetails?.token)) {
		return <Loader />;
	}

	return hasAssessmentEnded ? (
		<div className="relative h-full w-full bg-[#1f2023] ">
			<div className=" max-h-full overflow-y-scroll">
				{isMentorLoggedIn ? <PostInterviewComponent /> : null}

				<div className="mt-4 mb-6">
					{scorecardLoading ? (
						<Loader />
					) : (
						<ScorecardContainer
							scorecardTitle={meetingScoreCardData?.title ?? ''}
							data={{
								status: meetingScoreCardData?.meetingStatus ?? MeetingStatus.COMPLETED,
								meetingScore: meetingScoreCardData?.meetingScore ?? 0,
								skillScores: meetingScoreCardData?.allMeetingSkillScores ?? [],
								meetingId: meeting._id,
							}}
						/>
					)}
				</div>
			</div>
		</div>
	) : (
		<iframe
			title="assessment"
			className="h-full w-full"
			src={
				meeting.isAIInterview && ongoingAssessDetails?.interviewUrl
					? ongoingAssessDetails.interviewUrl
					: session?.role && ![UserRole.JOBSEEKER, UserRole.EXPERIENCED_JOBSEEKER].includes(session?.role)
					  ? `${ASSESS_PLATFORM_FRONTEND}/test?token=${assessmentToken}&remove_header=1&admin-token=${data?.token}`
					  : `${ASSESS_PLATFORM_FRONTEND}/test?token=${assessmentToken}&remove_header=1`
			}
		></iframe>
	);
};

export default QuestionsScreen;
